import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ROUTES } from 'config/routes';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** When true, shows a compact fallback suited for page outlets. */
  variant?: 'global' | 'page';
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, info.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.assign(ROUTES.HOME);
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const isGlobal = this.props.variant !== 'page';

    return (
      <Box
        role="alert"
        textAlign="center"
        py={isGlobal ? 10 : 6}
        px={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={2}
      >
        <ErrorOutlineIcon color="error" sx={{ fontSize: isGlobal ? 72 : 48 }} />
        <Typography variant={isGlobal ? 'h5' : 'h6'} fontWeight={700}>
          Algo salió mal
        </Typography>
        <Typography color="text.secondary" maxWidth={420}>
          Ocurrió un error inesperado. Podés reintentar o volver al inicio.
        </Typography>
        <Box display="flex" gap={1.5} flexWrap="wrap" justifyContent="center">
          <Button variant="contained" onClick={this.handleRetry}>
            Reintentar
          </Button>
          <Button variant="outlined" onClick={this.handleGoHome}>
            Volver al inicio
          </Button>
        </Box>
      </Box>
    );
  }
}

export default ErrorBoundary;
